import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import axios from "axios";
import {
  Trash2,
  Edit,
  Plus,
  Printer,
  X,
  Lightbulb,
  Loader2,
  Check,
} from "lucide-react";
import { IndentPDF } from "../../../components/Header/IndentPDF";
import PrinterButton from "../IndentComponents/PrinterButton";
import { useDispatch } from "react-redux";
import { fetchItems } from "../../../store/itemSlice";
import SearchRow from "../IndentComponents/SearchRow";
import Modal from "../IndentComponents/Modal";
import DeleteConfirmationModal from "../IndentComponents/DeleteConfirmationModal";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const calculatePromisedDate = (days, isEmergency) => {
  if (!days || isNaN(days)) return "";

  const today = new Date();
  const promiseDate = new Date(today);
  promiseDate.setDate(today.getDate() + parseInt(days));
  return promiseDate.toISOString().split("T")[0];
};


const L1ItemsTable = ({
  indentID,
  indentType,
  onDeleteItem,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1a: 0, l1: 0 },
  setPageLoading,
}) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);

  const [tableData, setTableData] = useState({
    categories: [],
    allItems: [],
    items: [],
    accountingUnits: [],
  });

  const [uiState, setUiState] = useState({
    modalOpen: false,
    loading: false,
    error: null,
    isSubmitting: false,
  });

  const [editingItem, setEditingItem] = useState(null);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  const initialNewItem = {
    categoryID: "",
    itemID: "",
    itemName: "",
    quantity: 1,
    stock: 0,
    remarks: "",
    normalDays: 0,
    emergencyDays: 0,
    promisedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
  };
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [newItem, setNewItem] = useState(initialNewItem);
  const [items, setItems] = useState([]);
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    itemIndex: null,
    itemName: "",
    indentItemID: null,
  });
  const [pdfError, setPdfError] = useState("");
  const dispatch = useDispatch();
  // Function to fetch indent data for PDF
  const fetchIndentData = async () => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l1page/indents/${indentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    // Fetch items with category information
    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l1page/indentitems/${indentID}`
    );
    const itemsData = await itemsResponse.json();

    console.log('Indent details:', indentDetails);

    return {
      indentId: indentID,
      creationDate:
        indentDetails?.CreationDate ||
        new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      PEApprovalDate: indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate || null,
      L1ApprovedDate: indentDetails?.L1ApprovedDate || null,
      L2ApprovalDate: indentDetails?.L2ApprovalDate || null,
      projectName: indentDetails?.ProjectName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      status: indentDetails?.Status || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      invoicingType: indentDetails?.TypeName || "N/A",
      // Add approval flags
      PO: indentDetails?.PO || 0,
      L1: indentDetails?.L1 || 0,
      L2: indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
      })),
    };
  };

  // Function to get PDF component
  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  useEffect(() => {
    if (indentID) {
      dispatch(fetchItems(indentID))
        .unwrap()
        .then((items) => {
          items.forEach((item) => {
            if (!item.description) {
              fetchItemDescription(item.itemID);
            }
          });
        })
        .catch((error) => {
          console.error("Error fetching items:", error);
        });
    }
  }, [dispatch, indentID]);

  // Modified handleDeleteItem to handle the confirmation flow
  const handleDeleteClick = (index, itemName, indentItemID) => {
    try {
      console.log("[L1ItemsTable] Delete click:", { index, itemName, indentItemID });
    } catch (_) {}
    setDeleteConfirmation({
      isOpen: true,
      itemIndex: index,
      itemName: itemName,
      indentItemID: indentItemID,
    });
  };

  const handleDeleteConfirm = async () => {
    const index = deleteConfirmation.itemIndex;
    const indentItemID = deleteConfirmation.indentItemID;
    const itemToDelete = tableData.items[index] || items.find(i => i.indentItemID === indentItemID);

    try {
      await axios.delete(
        `${API_BASE_URL}/l1page/indentitems/${indentItemID}`,
        {
          params: {
            indentID: indentID,
          },
        }
      );

      // Update the tableData state immediately
      setTableData(prev => ({
        ...prev,
        items: prev.items.filter((i) => i.indentItemID !== indentItemID)
      }));

      // Also update the items state for consistency
      setItems(prev => prev.filter((i) => i.indentItemID !== indentItemID));

      // Update change log
      setChangeLog(prev => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete]
      }));

      // Notify parent component
      onUpdateItem?.({
        type: "delete",
        item: itemToDelete,
        changeLog: {
          deletedItems: [itemToDelete]
        }
      });

      // Refresh the data to ensure consistency
      await fetchItems2();

      setUiState(prev => ({
        ...prev,
        error: null
      }));
    } catch (error) {
      console.error("Error deleting item:", error);
      let errorMessage = "Failed to delete item. Please try again.";

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = "Missing required information for deletion.";
            break;
          case 401:
            errorMessage = "Session expired. Please log in again.";
            break;
          case 403:
            errorMessage = "You don't have permission to delete this item.";
            break;
          case 404:
            errorMessage = "Item not found. Please refresh the page and try again.";
            break;
          case 500:
            errorMessage = "Server error while deleting item. Please try again.";
            break;
          default:
            errorMessage = error.response.data?.error || "Failed to delete item. Please try again.";
        }
      }

      setUiState(prev => ({
        ...prev,
        error: errorMessage
      }));
    } finally {
      setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "", indentItemID: null });
    }
  };


  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
  };

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      // Only fetch descriptions for items missing in cache to avoid repeat calls
      const missing = items.filter((it) => it.itemID && !itemDescriptions[it.itemID]);
      if (missing.length === 0) return;
      missing.forEach((item) => {
          fetchItemDescription(item.itemID);
      });
    }
  }, [items.length]); // Only depend on items.length, not the entire items array

  const formatQuantity = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }
    if (parts[1]?.length > 3) {
      return parts[0] + "." + parts[1].slice(0, 3);
    }
    return cleanValue;
  };

  const handleQuantityChange = (value) => {
    // Allow decimal values up to 3 places
    const regex = /^\d*\.?\d{0,3}$/;
    if (regex.test(value)) {
      setNewItem((prev) => ({
        ...prev,
        quantity: value,
      }));
    }
  };

  const fetchItems2 = useCallback(async () => {
    if (!indentID) return;

    setUiState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/l1page/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data.map((item) => ({
        indentItemID: item.IndentItemID,
        itemName: item.ItemName,
        quantity: item.Quantity,
        stock: item.Stock || 0,
        categoryID: item.CategoryID,
        itemID: item.ItemID,
        remarks: item.Description,
        promisedDate: item.PromisedDate,
        normalDays: item.NormalDays,
        emergencyDays: item.EmergencyDays,
        accountingUnitID: item.AccountingUnitID,
        unitname: item.UnitName,
      }));

      setTableData((prev) => ({ ...prev, items: fetchedItems }));
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to load items. Please try again.",
      }));
      console.error("Error fetching items:", error);
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  }, [indentID]);

  // Add real-time synchronization with polling
  useEffect(() => {
    if (!indentID) return;

    // Initial fetch
    fetchItems2();

    // Set up polling for real-time updates (every 30 seconds)
    const pollInterval = setInterval(() => {
      fetchItems2();
    }, 30000);

    // Cleanup interval on unmount or indentID change
    return () => {
      clearInterval(pollInterval);
    };
  }, [indentID]); // Only depend on indentID

  useEffect(() => {
    let isMounted = true;
    const fetchInitialData = async () => {
      setUiState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const [categoryRes, itemRes, accountingUnitsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
          axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
          axios.get(`${API_BASE_URL}/dropdown/maudrop`),
        ]);

        if (!isMounted) return;
        setCategories(categoryRes.data || []);
        setAllItems(itemRes.data || []);
        setTableData((prev) => ({
          ...prev,
          categories: categoryRes.data || [],
          allItems: itemRes.data || [],
          accountingUnits: accountingUnitsRes.data || [],
        }));

        // Only fetch items if we have an indentID
        if (indentID) {
        await fetchItems2();
        }
      } catch (error) {
        if (!isMounted) return;
        setUiState((prev) => ({
          ...prev,
          error: "Failed to load initial data. Please refresh the page.",
        }));
        console.error("Error fetching initial data:", error);
      } finally {
        if (!isMounted) return;
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchInitialData();
    return () => { isMounted = false; };
  }, [indentID]); // Only depend on indentID, not fetchItems2

  const handleItemChange = useCallback(
    (itemId) => {
      const parsedItemId = parseInt(itemId, 10);
      const selectedItem = filteredItems.find(
        (item) => item.ItemID === parsedItemId
      );

      if (selectedItem) {
        setNewItem((prev) => ({
          ...prev,
          itemID: parsedItemId,
          itemName: selectedItem.ItemName, // Add this line
          accountingUnitID: selectedItem.AccountingUnitID || "",
          accountingUnitName: selectedItem.AU || "",
          stock: selectedItem.Stock || 0,
        }));
      }
    },
    [filteredItems]
  );

  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setNewItem((prev) => ({
        ...prev,
        categoryID: "",
        itemID: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      }));
      setFilteredItems([]);
      return;
    }

    const parsedCategoryId = parseInt(categoryId, 10);
    const selectedCategory = tableData.categories.find(
      (cat) => cat.CategoryID === parsedCategoryId
    );

    if (selectedCategory) {
      // Use emergency days if indent type is emergency
      const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
      const days = isEmergency
        ? selectedCategory.EmergencyDays || 0
        : selectedCategory.NormalDays || 0;

      const promisedDate = calculatePromisedDate(days);

      setNewItem((prev) => ({
        ...prev,
        categoryID: parsedCategoryId,
        itemID: "",
        normalDays: selectedCategory.NormalDays || 0,
        emergencyDays: selectedCategory.EmergencyDays || 0,
        promisedDate: promisedDate,
        accountingUnitID: "",
        accountingUnitName: "",
      }));

      const filtered = tableData.allItems.filter(
        (item) => item.CategoryID === parsedCategoryId
      );
      setFilteredItems(filtered);
    }
  };

  const handleNewItemFieldChange = useCallback((field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = Math.round(parseFloat(value));
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else if (field === "normalDays") {
      const promisedDate = calculatePromisedDate(value);
      setNewItem((prev) => ({
        ...prev,
        [field]: value,
        promisedDate: promisedDate,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleAddNewItem = useCallback(async () => {
    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    if (!newItem.itemID || !newItem.categoryID) {
      setUiState((prev) => ({
        ...prev,
        error: "Please select both category and item",
      }));
      return;
    }

    setUiState((prev) => ({ ...prev, loading: true, error: null }));
    setPageLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/l1page/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: Math.round(newItem.quantity),
        Description: newItem.remarks || "",
        AccountingUnitID: newItem.accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
      });

      await fetchItems2();
      setNewItem(initialNewItem);
      setUiState((prev) => ({ ...prev, modalOpen: false, error: null }));

      setChangeLog((prev) => ({
        ...prev,
        addedItems: [...prev.addedItems, response.data],
      }));

      onUpdateItem?.({
        type: "add",
        item: response.data,
        changeLog,
      });
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to add item. Please try again.",
      }));
      console.error("Error adding item:", error);
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
      setPageLoading(false);
    }
  }, [newItem, indentID, fetchItems2, changeLog, onUpdateItem, setPageLoading]);

  const handleUpdateDescription = async (itemId, newDescription) => {
    try {
      await axios.put(`${API_BASE_URL}/viewTable/itemdescription/${itemId}`, {
        Description: newDescription,
        IndentID: indentID,
      });

      setItemDescriptions((prev) => ({
        ...prev,
        [itemId]: newDescription,
      }));

      setEditingItem((prev) => ({
        ...prev,
        description: newDescription,
        isEditingDescription: false,
      }));
    } catch (error) {
      console.error("Error updating description:", error);
      setError("Failed to update description. Please try again.");
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;

    setUiState((prev) => ({ ...prev, loading: true, error: null }));
    const formattedQuantity = Math.round(editingItem.quantity);

    console.log('L1 Frontend Update Request:', {
      indentItemID: editingItem.indentItemID,
      quantity: formattedQuantity,
      stock: editingItem.stock,
      description: editingItem.description
    });

    try {
      const response = await axios.put(
        `${API_BASE_URL}/l1page/indentitems/${editingItem.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: editingItem.stock,
          Description: editingItem.description,
        }
      );

      console.log('L1 Frontend Update Response:', response.data);
      
      // Update local description state
      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));

      // Update local state immediately for better UX
      const updatedItems = tableData.items.map((item) =>
        item.indentItemID === editingItem.indentItemID
          ? {
              ...item,
              quantity: formattedQuantity,
              stock: editingItem.stock,
              remarks: editingItem.description,
            }
          : item
      );

      setTableData((prev) => ({ ...prev, items: updatedItems }));
      setEditingItem(null);

      // Also refresh data from server to ensure consistency
      await fetchItems2();

      onUpdateItem?.({
        type: "update",
        item: editingItem,
        items: updatedItems,
      });

      // Show success message
      setUiState((prev) => ({
        ...prev,
        success: "Item updated successfully!",
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUiState((prev) => ({ ...prev, success: null }));
      }, 3000);

    } catch (error) {
      console.error("L1 Frontend Update Error:", error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to update item. Please try again.";
      
      setUiState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  };

  const refreshItemsData = async () => {
    if (!indentID) return;

    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/l1page/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data.map((item) => ({
        indentItemID: item.IndentItemID,
        itemName: item.ItemName,
        quantity: item.Quantity,
        stock: item.Stock || 0,
        categoryID: item.CategoryID,
        itemID: item.ItemID,
        remarks: item.Description,
        promisedDate: item.PromisedDate,
        normalDays: item.NormalDays,
        emergencyDays: item.EmergencyDays,
        accountingUnitID: item.AccountingUnitID,
        unitname: item.UnitName,
      }));

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error refreshing items:", error);
      setError("Failed to refresh items. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Removed refresh effects to prevent loops - data is now managed by parent component

  const handleFinalSubmit = async () => {
    setUiState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      await onSubmit?.();
      setChangeLog({
        addedItems: [],
        deletedItems: [],
        updatedItems: [],
      });
    } catch (error) {
      console.error("Error submitting:", error);
      setUiState((prev) => ({
        ...prev,
        error: "Failed to submit. Please try again.",
      }));
    } finally {
      setUiState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleItemSearch = useCallback(
    (selectedItem) => {
      // Handle null/clearing case
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "",
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          emergencyDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      // First set filtered items for the selected category
      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      // Get the normalDays from the category
      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );
      const normalDays = selectedCategory?.NormalDays || 0;
      const emergencyDays = selectedCategory?.EmergencyDays || 0;

      // Calculate dates based on indent type
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      const daysToAdd = indentType === 'normal' ? normalDays : emergencyDays;
      promisedDate.setDate(currentDate.getDate() + daysToAdd);
      // Update all state in one go
      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.AU || "",
        stock: selectedItem.Stock || 0,
        normalDays: normalDays,
        emergencyDays: emergencyDays,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
      }));
    },
    [allItems, categories]
  );

  const isSubmittedStage = useMemo(
    () => indentStatus.l1a === 1 && indentStatus.l1 === 1,
    [indentStatus]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A to open add modal
      if (e.ctrlKey && e.key.toLowerCase() === "a") {
        e.preventDefault(); // Prevent default browser "Select All" behavior
        if (!uiState.modalOpen && isDraft && isEditable && !isSubmittedStage) {
          setUiState((prev) => ({ ...prev, modalOpen: true }));
        }
      }

      // Ctrl+S to save item when modal is open
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault(); // Prevent the default browser save action
        if (uiState.modalOpen) {
          handleAddNewItem();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    uiState.modalOpen,
    isDraft,
    isEditable,
    isSubmittedStage,
    handleAddNewItem,
  ]);

  const renderDaysHeader = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <th className="px-4 py-3 text-right">
        {isEmergency ? "Emergency Days" : "Normal Days"}
      </th>
    );
  };

  const renderDaysCell = (item) => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <td className="px-4 py-3 text-right">
        {isEmergency ? item.emergencyDays : item.normalDays}
      </td>
    );
  };

  // Update the form in the modal to show appropriate days field
  const renderDaysInput = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <div className="col-span-1">
        <label className="block text-sm font-medium mb-1">
          {isEmergency ? "Days" : "Days"}
        </label>
        <input
          type="text"
          className="border rounded w-full px-3 py-2 bg-gray-100"
          value={
            isEmergency
              ? newItem.emergencyDays || "0"
              : newItem.normalDays || "0"
          }
          readOnly
        />
      </div>
    );
  };

  const renderActions = (item, index) => (
    <td className="px-4 py-3 text-center">
      <div className="flex justify-center space-x-2">
        {isDraft && isEditable && (
          <>
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => handleEditItem(item, index)}
            >
              <Edit size={18} />
            </button>
            <button
              type="button"
              className="text-red-500 hover:text-red-700"
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(index, item.itemName, item.indentItemID); }}
            >
              <Trash2 size={18} />
            </button>
          </>
        )}
      </div>
    </td>
  );

  const handleDescriptionChange = useCallback((e) => {
    setEditingItem((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  }, []);

  const renderDescriptionCell = useCallback((itemId) => {
    if (editingItem?.itemID === itemId) {
      return (
        <div className="flex items-center space-x-2">
          <input
            key={`description-${itemId}-${editingItem.description}`}
            type="text"
            className="border rounded px-2 py-1 text-sm w-32"
            value={editingItem.description}
            onChange={handleDescriptionChange}
            autoFocus
          />
        </div>
      );
    }

    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  }, [editingItem, itemDescriptions, loadingDescriptions, handleDescriptionChange, fetchItemDescription]);

  const handleEditItem = (item, index) => {
    // Ensure we fetch the description if it hasn't been loaded yet
    if (!itemDescriptions[item.itemID]) {
      fetchItemDescription(item.itemID);
    }

    setEditingItem({
      ...item,
      index: index,
      indentItemID: item.indentItemID,
      description: itemDescriptions[item.itemID] || "",
    });
    // Don't toggle itemsUpdated here; it triggers refresh effects unnecessarily
  };

  const L1DesktopRow = memo(function L1DesktopRow({
    item,
    index,
    isSubmittedStage,
    editingItem,
    renderDescriptionCell,
    renderDaysCell,
    onQuantityChange,
    onStockChange,
    onSave,
    onCancel,
    onEdit,
    onDelete,
  }) {
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors duration-200">
        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
        <td className="px-4 py-3 font-medium">{item.itemName}</td>
        <td className="px-4 py-3 text-center">{renderDescriptionCell(item.itemID)}</td>
        <td className="px-4 py-3 font-medium">{item.unitname}</td>
        {editingItem && editingItem.index === index ? (
          <>
            <td className="px-4 py-3">
              <input
                type="number"
                className="w-full border rounded px-2 py-1"
                value={editingItem.quantity}
                onChange={(e) => onQuantityChange(e.target.value)}
              />
            </td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-green-500 hover:text-green-700" onClick={onSave}>Save</button>
                  <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>Cancel</button>
                </div>
              </td>
            )}
          </>
        ) : (
          <>
            <td className="px-4 py-3 text-center">{Math.round(item.quantity)}</td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            {renderDaysCell(item)}
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button type="button" className="text-blue-500 hover:text-blue-700" onClick={() => onEdit(item, index)}>
                    <Edit size={18} />
                  </button>
                  <button type="button" className="text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); onDelete(index, item.itemName); }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            )}
          </>
        )}
      </tr>
    );
  });

  return (
    <div className="bg-white shadow-md rounded-lg px-4 border border-gray-200">
      {uiState.loading && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
          <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
        </div>
      )}

      {uiState.error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {uiState.error}
        </div>
      )}

      {uiState.success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {uiState.success}
        </div>
      )}

      <div className="flex items-center justify-between border-b">
        {/* Left side - Keyboard shortcuts */}
        {isDraft && isEditable && !isSubmittedStage && (
          <div className="text-sm text-gray-500">
            Keyboard shortcuts: Ctrl+A to add item
            {uiState.modalOpen ? ", Ctrl+S to save" : ""}
          </div>
        )}

        {/* Center - Heading */}
        <h2 className="text-xl font-bold text-gray-800 absolute left-1/2 transform -translate-x-1/2">
          Indent Items
        </h2>

        {/* Right side - Action buttons */}
        <div className="flex items-center space-x-2">
          <PrinterButton
            getPDFComponent={getPDFComponent}
            filename={`indent-${indentID}`}
            fetchData={fetchIndentData}
            onError={(error) => setPdfError("Failed to generate PDF. Please try again.")}
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
            title="Print Invoice"
          />

          {!isSubmittedStage && isDraft && isEditable && (
            <button
              className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
              onClick={() => setUiState((prev) => ({ ...prev, modalOpen: true }))}
              title="Add Item"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>


      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-right">Stock</th>
              {renderDaysHeader()}
              {!isSubmittedStage && (
                <th className="px-4 py-3 text-center w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.items.length > 0 ? (
              tableData.items.map((item, index) => (
                <L1DesktopRow
                  key={index}
                  item={item}
                  index={index}
                  isSubmittedStage={isSubmittedStage}
                  editingItem={editingItem}
                  renderDescriptionCell={renderDescriptionCell}
                  renderDaysCell={renderDaysCell}
                  onQuantityChange={(val) => setEditingItem((prev) => ({ ...prev, quantity: val }))}
                  onSave={handleUpdateItem}
                  onCancel={() => setEditingItem(null)}
                  onEdit={handleEditItem}
                  onDelete={(index, itemName) => handleDeleteClick(index, itemName, item.indentItemID)}
                />
              ))
            ) : (
              <tr>
                <td colSpan={!isSubmittedStage ? 7 : 6} className="text-center text-gray-500 py-6">
                  Loading items...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-between items-center">
        <div className="flex space-x-4">
          {isSubmittedStage ? (
            <button
              className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
              disabled
            >
              Submitted
            </button>
          ) : (
            <>
              {isDraft && isEditable && (
                <>
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={onSaveDraft}
                    disabled={
                      tableData.items.length === 0 ||
                      !isDraft ||
                      uiState.isSubmitting
                    }
                  >
                    Save Draft
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    onClick={handleFinalSubmit}
                    disabled={
                      tableData.items.length === 0 ||
                      !isDraft ||
                      uiState.isSubmitting
                    }
                  >
                    {uiState.isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <span>Submit Indent</span>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {uiState.modalOpen && (
        <Modal
          onClose={() => {
            setUiState((prev) => ({ ...prev, modalOpen: false, error: null }));
          }}
        >
          <div className="flex flex-col">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <h2 className="text-xl font-bold text-center mb-4">Add New Item</h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddNewItem();
              }}
            >
              <SearchRow
                allItems={allItems.map((item) => ({
                  ...item,
                  CategoryName: categories.find(
                    (cat) => cat.CategoryID === item.CategoryID
                  )?.CategoryName,
                }))}
                onSelectItem={handleItemSearch}
                selectedItemId={newItem.itemID}
              />

              <div className="grid grid-cols-12 gap-4 mb-4">
                <div className="col-span-3">
                  <label className="block text-sm font-medium mb-1">
                    Accounting Unit
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2 bg-gray-50"
                    value={newItem.accountingUnitName || "Not set"}
                    readOnly
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Up to 3 decimals"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Stock
                  </label>
                  <input
                    type="number"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.stock}
                    onChange={(e) =>
                      handleNewItemFieldChange(
                        "stock",
                        parseFloat(e.target.value)
                      )
                    }
                    required
                  />
                </div>
                {renderDaysInput()}
                <div className="col-span-4">
                      <label className="block text-sm font-medium mb-1">
                        Promised Date
                      </label>
                      <input
                        type="text"
                        className="border rounded w-full px-3 py-2 bg-gray-100"
                        value={
                          newItem.promisedDate
                            ? (() => {
                                const promisedDate = new Date(
                                  newItem.promisedDate
                                );
                                const formattedDate =
                                  promisedDate.toLocaleDateString();
                                const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
                                const daysUsed = isEmergency
                                  ? newItem.emergencyDays || 0
                                  : newItem.normalDays || 0;

                                return `${formattedDate} (${daysUsed} ${
                                  isEmergency
                                    ? "Emergency"
                                    : "Normal"
                                } Days)`;
                              })()
                            : "Not set"
                        }
                        readOnly
                      />
                    </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium">Remarks</label>
                <textarea
                  key={newItem.itemID} // Add a stable key to force re-render
                  className="border rounded w-full px-3 py-2"
                  value={newItem.remarks}
                  onChange={(e) =>
                    handleNewItemFieldChange("remarks", e.target.value)
                  }
                  placeholder="Optional remarks about the item"
                  rows="3"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  id="addItemButton"
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded"
                >
                  Add Item
                </button>
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                  onClick={() =>
                    setUiState((prev) => ({ ...prev, modalOpen: false }))
                  }
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen === true}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirmation.itemName}
      />
    </div>
  );
};

export default L1ItemsTable;
